/**
 * Chat-Sidebar — Session-History links wie ChatGPT.
 *
 * - Listet alle Vault-Sessions chronologisch absteigend (heute → ältere).
 * - Client-Side fuzzy filter über die Titel/Datum.
 * - Klick auf Session → Replay-View rechts rendert den Markdown read-only.
 * - "Neue Session" → invalidiert Replay, ruft onNewSession-Callback.
 * - Mobile: Drawer via Hamburger-Toggle (slide-in animiert).
 */

import { fetchSessionMarkdown, listSessions, type SessionFile } from "./api";

export interface SidebarHandlers {
  /** Aufgerufen wenn User "Neue Session" klickt — UI soll zum Voice-Modus zurück. */
  onNewSession(): void;
  /** Aufgerufen wenn User auf eine alte Session klickt — UI soll Replay anzeigen. */
  onSelectSession(file: SessionFile, markdown: string): void;
  /** Aufgerufen wenn Replay verlassen werden soll (z.B. User klickt "Neue Session"). */
  onLeaveReplay?(): void;
}

export class Sidebar {
  readonly root: HTMLElement;
  private listEl: HTMLElement;
  private searchInput: HTMLInputElement;
  private toggleBtn: HTMLButtonElement;
  private backdrop: HTMLElement;
  private handlers: SidebarHandlers;
  private files: SessionFile[] = [];
  private filter = "";
  private activeName: string | null = null;
  private loaded = false;

  constructor(parent: HTMLElement, handlers: SidebarHandlers) {
    this.handlers = handlers;
    const wrap = document.createElement("aside");
    // KEIN "glass": backdrop-filter:blur + transform:translateX triggert einen
    // WebKit-Bug der die Sidebar-Inhalte (Session-Liste) selbst verschwommen/
    // unklickbar macht. .sidebar hat ohnehin eigenen opaken Hintergrund.
    wrap.className = "sidebar";
    wrap.setAttribute("aria-label", "Session-Verlauf");
    wrap.innerHTML = `
      <header class="sidebar-head">
        <button type="button" class="sidebar-new" id="sidebarNew" aria-label="Neue Session">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
          <span>Neue Session</span>
        </button>
        <button type="button" class="icon-btn ghost sidebar-close" id="sidebarClose" aria-label="Sidebar schließen">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </header>
      <div class="sidebar-search">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
        <input type="search" id="sidebarSearch" placeholder="Suchen …" autocomplete="off" spellcheck="false" />
      </div>
      <div class="sidebar-list" id="sidebarList" role="list">
        <p class="sidebar-empty">Lade Sessions …</p>
      </div>
    `;
    parent.appendChild(wrap);
    this.root = wrap;
    this.listEl = wrap.querySelector("#sidebarList") as HTMLElement;
    this.searchInput = wrap.querySelector("#sidebarSearch") as HTMLInputElement;

    // Hamburger-Toggle Button — wird ans .top-actions in main.ts injiziert, hier
    // erzeugen wir nur ein detached Element; main.ts hängt es ein.
    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "icon-btn icon-btn-square sidebar-toggle";
    toggle.id = "sidebarToggle";
    toggle.setAttribute("aria-label", "Sidebar öffnen");
    toggle.setAttribute("aria-controls", "sidebar");
    toggle.innerHTML = `
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <line x1="3" y1="6" x2="21" y2="6"/>
        <line x1="3" y1="12" x2="21" y2="12"/>
        <line x1="3" y1="18" x2="21" y2="18"/>
      </svg>
    `;
    this.toggleBtn = toggle;

    // Backdrop (Mobile only, fängt outside-Clicks)
    const back = document.createElement("div");
    back.className = "sidebar-backdrop";
    back.id = "sidebarBackdrop";
    // MUSS in denselben Container wie die Sidebar (parent), NICHT an document.body:
    // .stage hat position:relative;z-index:0 → eigener Stacking-Context. Lag die
    // Backdrop an body (z-79), überdeckte sie die ganze .stage (z-0) inkl. der
    // Sidebar (z-80, die nur INNERHALB .stage zählt) → Session-Liste verschwommen
    // + unklickbar. Im selben Container ordnet z-index korrekt: Sidebar 80 > Backdrop 79.
    parent.appendChild(back);
    this.backdrop = back;

    wrap.id = "sidebar";

    // Listener
    (wrap.querySelector("#sidebarNew") as HTMLButtonElement).addEventListener("click", () => {
      this.activeName = null;
      this.handlers.onLeaveReplay?.();
      this.handlers.onNewSession();
      this.renderList();
      this.close();
    });
    (wrap.querySelector("#sidebarClose") as HTMLButtonElement).addEventListener("click", () => this.close());
    this.toggleBtn.addEventListener("click", () => this.toggle());
    this.backdrop.addEventListener("click", () => this.close());
    this.searchInput.addEventListener("input", () => {
      this.filter = this.searchInput.value.trim().toLowerCase();
      this.renderList();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && wrap.classList.contains("is-open")) this.close();
    });
  }

  /** Hamburger-Button — main.ts hängt das ans .top-actions. */
  getToggleButton(): HTMLButtonElement {
    return this.toggleBtn;
  }

  async refresh(): Promise<void> {
    try {
      this.files = await listSessions();
      this.loaded = true;
      this.renderList();
    } catch (err) {
      this.listEl.innerHTML = `<p class="sidebar-empty sidebar-err">Konnte Sessions nicht laden: ${escapeHtml(
        err instanceof Error ? err.message : String(err),
      )}</p>`;
    }
  }

  open(): void {
    this.root.classList.add("is-open");
    this.backdrop.classList.add("is-open");
    this.toggleBtn.setAttribute("aria-expanded", "true");
    // Lazy-load beim ersten Öffnen.
    if (!this.loaded) void this.refresh();
  }

  close(): void {
    this.root.classList.remove("is-open");
    this.backdrop.classList.remove("is-open");
    this.toggleBtn.setAttribute("aria-expanded", "false");
  }

  toggle(): void {
    if (this.root.classList.contains("is-open")) this.close();
    else this.open();
  }

  isOpen(): boolean {
    return this.root.classList.contains("is-open");
  }

  // ── Internal ────────────────────────────────────────────────────────────

  private renderList(): void {
    if (!this.loaded) return;
    const groups = this.groupByDay(this.applyFilter(this.files));
    if (groups.length === 0) {
      this.listEl.innerHTML = `<p class="sidebar-empty">Keine Sessions${this.filter ? " gefunden" : " bisher"}.</p>`;
      return;
    }
    const html: string[] = [];
    for (const { label, items } of groups) {
      html.push(`<div class="sidebar-group"><h4 class="sidebar-group-label">${escapeHtml(label)}</h4>`);
      for (const f of items) {
        const isActive = f.name === this.activeName ? " is-active" : "";
        const title = f.title || "(ohne Titel)";
        html.push(`
          <button type="button" class="sidebar-item${isActive}" data-name="${escapeAttr(f.name)}" role="listitem">
            <span class="sidebar-item-title">${escapeHtml(title)}</span>
            <span class="sidebar-item-meta">${escapeHtml(f.time || "")}${f.label && f.label !== "realtime" ? " · " + escapeHtml(f.label) : ""}</span>
          </button>
        `);
      }
      html.push(`</div>`);
    }
    this.listEl.innerHTML = html.join("");
    for (const btn of this.listEl.querySelectorAll<HTMLButtonElement>(".sidebar-item")) {
      btn.addEventListener("click", () => {
        const name = btn.dataset.name || "";
        const file = this.files.find((f) => f.name === name);
        if (!file) return;
        void this.selectSession(file);
      });
    }
  }

  private async selectSession(file: SessionFile): Promise<void> {
    try {
      const md = await fetchSessionMarkdown(file.name);
      this.activeName = file.name;
      this.renderList();
      this.handlers.onSelectSession(file, md);
      this.close();
    } catch (err) {
      this.listEl.insertAdjacentHTML(
        "afterbegin",
        `<p class="sidebar-err">Konnte Session nicht laden: ${escapeHtml(
          err instanceof Error ? err.message : String(err),
        )}</p>`,
      );
    }
  }

  private applyFilter(files: SessionFile[]): SessionFile[] {
    if (!this.filter) return files;
    const q = this.filter;
    return files.filter((f) => {
      const hay = `${f.title} ${f.date} ${f.time} ${f.label}`.toLowerCase();
      return hay.includes(q);
    });
  }

  private groupByDay(files: SessionFile[]): { label: string; items: SessionFile[] }[] {
    if (files.length === 0) return [];
    const today = new Date();
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const fmtDay = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const todayKey = fmtDay(today);
    const yKey = fmtDay(yesterday);
    const last7Cut = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const labelFor = (date: string): string => {
      if (!date) return "Älter";
      if (date === todayKey) return "Heute";
      if (date === yKey) return "Gestern";
      const d = new Date(date);
      if (!Number.isNaN(d.getTime()) && d > last7Cut) return "Letzte 7 Tage";
      return date.slice(0, 7); // YYYY-MM
    };

    const map = new Map<string, SessionFile[]>();
    const order: string[] = [];
    for (const f of files) {
      const lab = labelFor(f.date);
      if (!map.has(lab)) {
        map.set(lab, []);
        order.push(lab);
      }
      map.get(lab)!.push(f);
    }
    return order.map((label) => ({ label, items: map.get(label)! }));
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      case "'": return "&#39;";
      default: return c;
    }
  });
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}

// ── Replay-Renderer ─────────────────────────────────────────────────────────

export interface ReplayTurn {
  role: "you" | "jarvis" | "system";
  time: string;
  text: string;
}

/**
 * Parsed das von vault-persist.ts erzeugte Markdown.
 * Format pro Turn:
 *   ### HH:MM:SS — YOU
 *   <leerzeile>
 *   <text bis nächstes "### " oder "---">
 *   ### HH:MM:SS — JARVIS
 *   <leerzeile>
 *   <text>
 */
export function parseSessionMarkdown(md: string): ReplayTurn[] {
  const turns: ReplayTurn[] = [];
  const lines = md.split("\n");
  let i = 0;
  // Skip Header bis zum ersten "---"
  while (i < lines.length && !/^---\s*$/.test(lines[i])) i++;
  if (i < lines.length) i++; // skip the --- line

  while (i < lines.length) {
    const headerMatch = lines[i].match(/^###\s+(\d{2}:\d{2}:\d{2})\s+—\s+(YOU|JARVIS)\s*$/);
    if (!headerMatch) {
      i++;
      continue;
    }
    const time = headerMatch[1];
    const role = headerMatch[2] === "YOU" ? "you" : "jarvis";
    i++;
    // Skip eine Leerzeile
    if (i < lines.length && lines[i].trim() === "") i++;
    // Body bis nächster Header, "---", oder "**Tool-Calls"
    const body: string[] = [];
    while (i < lines.length) {
      const l = lines[i];
      if (/^###\s+\d{2}:\d{2}:\d{2}/.test(l)) break;
      if (/^---\s*$/.test(l)) {
        i++;
        break;
      }
      if (/^\*\*Tool-Calls\s+in\s+diesem\s+Turn:\*\*/.test(l)) {
        // Tool-Liste überspringen — bis Leerzeile oder ---
        while (i < lines.length && lines[i].trim() !== "" && !/^---/.test(lines[i])) i++;
        continue;
      }
      body.push(l);
      i++;
    }
    const text = body.join("\n").trim();
    if (text && !/^_\(.*\)_$/.test(text)) {
      turns.push({ role, time, text });
    }
  }
  return turns;
}

/**
 * Rendert ein Replay (read-only) in das angegebene Element.
 * Nutzt die gleichen Bubble-Styles wie das Live-Transcript.
 */
export function renderReplay(
  container: HTMLElement,
  file: SessionFile,
  markdown: string,
): void {
  const turns = parseSessionMarkdown(markdown);
  const header = `
    <div class="replay-header">
      <span class="replay-pill">REPLAY</span>
      <span class="replay-title">${escapeHtml(file.title || "(ohne Titel)")}</span>
      <span class="replay-meta">${escapeHtml(file.date)} · ${escapeHtml(file.time)}</span>
    </div>
  `;
  const bubbles = turns.length
    ? turns
        .map((t) => {
          const roleLabel = t.role === "you" ? "Du" : "JARVIS";
          return `
            <article class="bubble bubble-${t.role}">
              <span class="bubble-role">${roleLabel} · ${escapeHtml(t.time)}</span>
              <span class="bubble-body">${escapeHtml(t.text)}</span>
            </article>
          `;
        })
        .join("")
    : `<p class="replay-empty">Keine Turns in dieser Session.</p>`;
  container.innerHTML = `
    <div class="transcript replay-transcript">
      ${header}
      <div class="transcript-list" role="log">${bubbles}</div>
    </div>
  `;
  // Scroll oben — replay startet von vorne.
  const list = container.querySelector(".transcript-list") as HTMLElement | null;
  if (list) list.scrollTop = 0;
}
